// src/Marketing/MarketingSite.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../styles.css";
import QuizScreen from "../Quizscreen";
import brainBg from "./Brainbackground.png";

export default function MarketingSite() {
  // Visual constants
  const PLATE_BG = "rgba(10,14,24,0.62)";
  const MEDIA_AR = 2017 / 1096; // ~1.84
  const HERO_MS = 1500;

  const p = (...lines) => lines.map((line) => `• ${line}`).join("\n");

  // ------------------------ SLIDES ------------------------
  const slides = useMemo(
    () => [
      {
        id: "s1",
        bg: brainBg,
        question: "Welcome to the best Pub Trivia software in the world",
        answers: [
          "Power in the Cloud",
          "Trivia Without Limits",
          "Epic Gameplay",
          "Run More Venues, Stress Less",
          "Immersive Player Experience",
          "Powerful Quiz Development",
        ],
        questionByOption: [
          p(
            "100% Cloud-Based software.\n",
            "Instant updates keep every venue in sync automatically.\n",
            "Easily maintain consistent corporate branding at scale.\n",
            "Instant updates keep every venue in sync automatically.\n",
            "More easily contain your intellectual property without the need to physically distribute files.\n",
          ),
          p(
            "Unlimited instant scalability for quizes, players and devices. \n",
            "Hybrid-Ready. Combine in-venue teams with remote players for flexible event formats.\n",
            "Run shows from pubs, theatres, conferences or even your lounge room.\n",
            "Launch simultaneous shows in multiple venues from one dashboard.\n"
          ),
          p(
            "Huge variety of question types from 'Vanishing Multi-Choice', 'Jeopardy' and 'Nearest Answer' to 'Bet Your Points'.\n",
            "Dynamic team score screens and leaderboards featuring actual images of the teams.\n", 
            "Advanced scoring features including more fast-answer bonuses, penalty points and intelligent typo tollerance.\n",
            "Dozens of new features planned imminently from Team Login, double-point jokers, 'Unicorn team' answer bonuses, selection of round topic for last place and more!\n"
          ),
          p(
            "No complex Host training required. Launch a fully interactive quiz in seconds with one-click.\n",
            "100% browser-based hosting means no installs, file transfers, patch days or device setup.\n",
            "Cloud-synced state means a failed device can be swapped in seconds (for hosts or players) without losing progress.\n",
            "Instant updates and media handling remove technical headaches.\n",
            "Track team numbers across venues and quiz-types.\n"
          ),
          p(
            "Responsive design means the player screen looks and feels great on any device, from a phone in portrait mode to a giant projector.\n",
            "Media rich experience supporting dozens of audio, image and video assets.\n",
            "Vibrant, mobile-friendly interface with smooth animations and large, clear answer options.\n",
            "Players add the app to their home screen with no app store needed.\n",
            "Interactive features including Jigsaw image reveal and Vanishing Choices.\n",
          ),
          p(
            "Intuitive and powerful integrated, Cloud-Based Quiz development tool that Quizmasters can test, edit, share or publish instantly.\n",
            "Completely customizable Quiz creation tools from backgrounds to media, points and scores for individual questions and slides.\n",
            "Upload legacy question sets via CSV import.\n",
            "A Database of re-usable, taggable questions makes Quiz building simple.\n",
            "Detailed stats tracking helps balance question difficulty and keep audiences coming back. Find softballs and trick questions instantly.\n",
          ),
        ],

        // Default media for the slide
        media: { type: "video", src: "/marketing/Pub trivia on big screen.mp4" },

        // Per-option media (one per answer). Missing entries fall back to slide.media.
        mediaByOption: [
          { type: "video", src: "/marketing/Pub trivia on big screen.webm" },
          { type: "video", src: "/marketing/using smarthphone in a pub.webm" },
          { type: "image", src: "/marketing/vanishing_compressed.png" },
          { type: "image", src: "/marketing/nearest_answer question scores_compressed.png" },
          { type: "video", src: "/marketing/vanishing answer and scores.webm" },
          { type: "image", src: "/marketing/easy_stats_compressed.png" },
        ],
      },
    ],
    []
  );

  // ----------------- ENGINE + LAYOUT STATE -----------------
  const hostRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [heroActive, setHeroActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [bgEl, setBgEl] = useState(null);

  const [sideRect, setSideRect] = useState(null);
  const [detailText, setDetailText] = useState("");
  const [activeMedia, setActiveMedia] = useState(null);
  const DEFAULT_PROMPT = "click an option for more information";

  // Adaptive left shift so answers never get squeezed
  const [listShift, setListShift] = useState(0);
  const computeShift = () =>
    Math.min(420, Math.max(180, Math.round(window.innerWidth * 0.18))); // gentler than before

  const s = slides[idx];

  // Reset media to slide default when slide changes
  useEffect(() => {
    setActiveMedia(s.media || null);
  }, [idx, s.media]);

  // ------------------ Helper: small/top question ------------------
  const topPromptEl = () => {
    const root = hostRef.current;
    if (!root) return null;
    const prompts = Array.from(
      root.querySelectorAll(".quiz-bg .question-prompt")
    );
    const isInHero = (el) =>
      el.closest(".question-intro") ||
      el.closest(".hero-hold") ||
      el.closest(".question-hero");
    return prompts.find((el) => !isInHero(el)) || null;
  };
  const setTopPromptText = (text) => {
    const el = topPromptEl();
    if (el) el.textContent = text || "";
  };

  // ------------------ Background setup ------------------
  useEffect(() => {
    const root = hostRef.current;
    if (!root) return;
    const bg = root.querySelector(".quiz-bg");
    if (bg && s.bg) {
      bg.style.background = `url(${s.bg}) center/cover no-repeat`;
      bg.style.backgroundColor = "transparent";
      if (!bg.style.position) bg.style.position = "relative";
      setBgEl(bg);
    }
    document.body.style.background = "transparent";
  }, [idx, s.bg]);

  // ------------------ Left shift ------------------
  useEffect(() => {
    const updateShift = () => setListShift(computeShift());
    updateShift();
    window.addEventListener("resize", updateShift);
    return () => window.removeEventListener("resize", updateShift);
  }, []);
  const applyListShift = () => {
    const root = hostRef.current;
    if (!root) return;
    const list = root.querySelector("ol.answer-list");
    if (!list) return;
    list.style.transform = `translateX(-${listShift}px)`;
    list.style.willChange = "transform";
    list.style.transition = "transform 80ms ease-out";
  };
  useEffect(() => {
    applyListShift();
    if (!heroActive) measureSide();
  }, [listShift, heroActive, idx]);

  // ------------------ Answer snap + fallback overlay ------------------
  const [showOverlay, setShowOverlay] = useState(false);
  const answersPresent = () => {
    const root = hostRef.current;
    if (!root) return false;
    return root.querySelectorAll("ol.answer-list > li").length > 0;
  };
  const hideHeroLayers = () => {
    const root = hostRef.current;
    if (!root) return;
    root
      .querySelectorAll(".question-intro, .hero-hold, .question-hero")
      .forEach((el) => {
        el.style.display = "none";
      });
  };
  const snapAnswersIn = () => {
    const root = hostRef.current;
    if (!root) return;
    const list = root.querySelector("ol.answer-list");
    if (list) {
      list.style.animation = "none";
      list.style.transition = "none";
      list.style.opacity = "1";
      root
        .querySelectorAll(
          "ol.answer-list > li, .answer-option, .answers, .answer"
        )
        .forEach((el) => {
          el.style.animation = "none";
          el.style.transition = "none";
          el.style.opacity = "1";
          el.style.transform = "none";
        });
      applyListShift();
      setShowOverlay(false);
    } else {
      setShowOverlay(true);
    }
  };

  // ------------------ Slide change / hero gate ------------------
  useEffect(() => {
    setSelectedIdx(null);
    setDetailText("");
    setHeroActive(true);
    setCurrentStep(0);
    requestAnimationFrame(() => setCurrentStep(1));

    const root = hostRef.current;
    if (!root) {
      setHeroActive(false);
      return;
    }

    const finish = () => {
      hideHeroLayers();
      setHeroActive(false);
      setTopPromptText(s.question);
      snapAnswersIn();
      measureSide();
      setDetailText(DEFAULT_PROMPT);
      setTimeout(measureSide, 40);
    };

    const obs = new MutationObserver(() => {
      if (!heroActive) return;
      const heroGone =
        !root.querySelector(".question-intro, .hero-hold, .question-hero");
      if (heroGone || answersPresent()) {
        obs.disconnect();
        finish();
      }
    });
    obs.observe(root, { subtree: true, childList: true });

    const t = setTimeout(() => {
      obs.disconnect();
      finish();
    }, HERO_MS);

    return () => {
      clearTimeout(t);
      obs.disconnect();
    };
  }, [idx, s.question]); // eslint-disable-line

  useEffect(() => {
    if (!showOverlay) return;
    const t = setInterval(() => {
      if (answersPresent()) {
        snapAnswersIn();
        clearInterval(t);
      }
    }, 50);
    return () => clearInterval(t);
  }, [showOverlay]);

  // ------------------ Responsive side panel ------------------
  const measureSide = () => {
    const root = hostRef.current;
    if (!root) return;
    const list = root.querySelector("ol.answer-list");
    const bg = root.querySelector(".quiz-bg");
    if (!list || !bg) return;

    const listRect = list.getBoundingClientRect();
    const bgRect = bg.getBoundingClientRect();

    const gap = 16;

    // Desired side width scaled to screen, within safe bounds
    const minSideW = 560; // was 680: smaller so mid-size screens keep a right panel
    const targetSideW = Math.min(
      840,
      Math.max(minSideW, Math.floor(window.innerWidth * 0.38))
    );

    const availableRight = bgRect.right - (listRect.right + gap) - 16;

    // If we don't have enough horizontal room, go to BELOW layout
    if (availableRight < minSideW) {
      const underWidth = Math.min(listRect.width, bgRect.width - 24);
      const underLeft = Math.max(12, listRect.left - bgRect.left);
      const underTop = listRect.bottom - bgRect.top + 12;

      const totalBelow = Math.max(360, bgRect.bottom - (listRect.bottom + 24));
      const minText = 200;
      const minMedia = 200;
      let textH = Math.max(minText, Math.floor(totalBelow * 0.45));
      let mediaH = Math.max(minMedia, totalBelow - textH - 12);

      const mediaFromAR = Math.round(underWidth / MEDIA_AR);
      mediaH = Math.max(minMedia, Math.min(mediaH, mediaFromAR));

      setSideRect({
        layout: "below",
        left: underLeft,
        top: underTop,
        width: underWidth,
        textH,
        mediaH,
      });
      return;
    }

    // RIGHT-of-answers layout
    const width = Math.min(targetSideW, availableRight);
    const left = listRect.right - bgRect.left + gap;
    const top = listRect.top - bgRect.top;

    // Vertical fit on the right
    const availV = bgRect.bottom - listRect.top - 24;
    const minText = 200;
    const minMedia = 200;

    // Start with a 40/60 split (text/media), then clamp
    let textH = Math.max(minText, Math.min(420, Math.floor(availV * 0.42)));
    let mediaH = Math.max(minMedia, Math.round(width / MEDIA_AR));

    // If overflow, reduce text first then media
    if (textH + 12 + mediaH > availV) {
      const overflow = textH + 12 + mediaH - availV;
      const textCanLose = Math.max(0, textH - minText);
      const takeFromText = Math.min(overflow, textCanLose);
      textH -= takeFromText;

      const still = textH + 12 + mediaH - availV;
      if (still > 0) mediaH = Math.max(minMedia, mediaH - still);
    }

    setSideRect({ layout: "right", left, top, width, textH, mediaH });
  };

  useEffect(() => {
    if (!heroActive) {
      const t = setTimeout(measureSide, 30);
      return () => clearTimeout(t);
    }
  }, [idx, heroActive, selectedIdx]);
  useEffect(() => {
    const onResize = () => measureSide();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ------------------ Keyboard nav ------------------
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight")
        setIdx((x) => Math.min(slides.length - 1, x + 1));
      if (e.key === "ArrowLeft") setIdx((x) => Math.max(0, x - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slides.length]);

  // ------------------ Click handling (highlight + text + media) ------------------
  useEffect(() => {
    const root = hostRef.current;
    if (!root) return;

    const onClick = (e) => {
      const li = e.target.closest("ol.answer-list > li, .mk-overlay-list > li");
      if (!li) return;

      const items = Array.from(root.querySelectorAll("ol.answer-list > li"));
      const overlayItems = Array.from(
        root.querySelectorAll(".mk-overlay-list > li")
      );

      let pickIdx = items.indexOf(li);
      if (pickIdx < 0) pickIdx = overlayItems.indexOf(li);
      if (pickIdx < 0) return;

      setSelectedIdx(pickIdx);

      const text =
        (s.questionByOption && s.questionByOption[pickIdx]) ||
        DEFAULT_PROMPT;
      setDetailText(text);

      const optionMedia =
        (s.mediaByOption && s.mediaByOption[pickIdx]) || s.media || null;
      setActiveMedia(optionMedia);

      // Add highlight classes (these are what our CSS below will style)
      if (items.length) {
        root
          .querySelectorAll("ol.answer-list > li .answer-option")
          .forEach((el, i) => {
            el.classList.remove("correct", "pulse-correct");
            if (i === pickIdx) el.classList.add("correct", "pulse-correct");
          });
      }
    };

    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, [s]); // MarketingSite already attaches these classes:contentReference[oaicite:2]{index=2}

  // ------------------ Build q ------------------
  const q = useMemo(() => {
    const order = s.answers.map((_, i) => i);
    return {
      type: "MULTICHOICE",
      question: s.question,
      answers: s.answers,
      answersOrder: order,
      time: 0,
      BACKGROUND_URL: s.bg,
      VIDEO_URL: s.video || undefined,
    };
  }, [s]);

  const visibleAnswerIndexes = useMemo(
    () => s.answers.map((_, i) => i),
    [s.answers]
  );

  const mediaToShow = activeMedia || s.media || null;

  return (
    <div
      id="marketing-host-shell"
      style={{ position: "fixed", inset: 0, background: "transparent" }}
    >
      <style>{`
        /* Clamp hero to ${HERO_MS}ms and kill delays */
        #marketing-host .question-intro,
        #marketing-host .question-intro *,
        #marketing-host .question-hero,
        #marketing-host .hero-hold,
        #marketing-host .question-hero *,
        #marketing-host .hero-hold * {
          animation-duration: ${HERO_MS}ms !important;
          animation-delay: 0s !important;
          transition-delay: 0s !important;
          animation-fill-mode: both !important;
        }
        ${heroActive ? `#marketing-host .quiz-bg .question-prompt { visibility: hidden !important; }` : ""}

        /* Remove stagger once mounted */
        #marketing-host ol.answer-list,
        #marketing-host ol.answer-list > li,
        #marketing-host .answer-option,
        #marketing-host .answers,
        #marketing-host .answer {
          animation-delay: 0s !important;
          transition-delay: 0s !important;
          animation-duration: 0.01s !important;
          transition-duration: 0.01s !important;
        }

        /* Strong host styling */
        #marketing-host .answer-option,
        #marketing-host .answer-option .host-answer-text {
          font-family: var(--main-font, 'Arial Black', Impact, Arial, sans-serif) !important;
          text-shadow:
            -2px -2px 0 #111,
             2px -2px 0 #111,
            -2px  2px 0 #111,
             2px  2px 0 #111 !important;
        }

        /* ===========================================================
           BIGGER + FLASHIER GREEN HALO (scoped to MarketingSite only)
           =========================================================== */

        /* Thicker base ring when correct */
        #marketing-host .answer-option.correct {
          border-width: 6px !important;           /* thicker than global 2px */
          border-color: #22c55e !important;
          box-shadow:
            0 0 0 8px rgba(34,197,94,0.20),        /* outer glow ring */
            0 0 0 0 rgba(34,197,94,0.0),
            0 4px 16px rgba(0,0,0,0.45);
        }

        /* Stronger pulsing flash */
        @keyframes mkStrongPulse {
          0% {
            box-shadow:
              0 0 0 10px rgba(34,197,94,0.30),
              0 0 32px rgba(34,197,94,0.55),
              0 4px 16px rgba(0,0,0,0.45);
            filter: brightness(1.05);
          }
          50% {
            box-shadow:
              0 0 0 20px rgba(34,197,94,0.22),
              0 0 54px rgba(34,197,94,0.75),
              0 8px 20px rgba(0,0,0,0.50);
            filter: brightness(1.18);
          }
          100% {
            box-shadow:
              0 0 0 10px rgba(34,197,94,0.30),
              0 0 32px rgba(34,197,94,0.55),
              0 4px 16px rgba(0,0,0,0.45);
            filter: brightness(1.05);
          }
        }

        /* Apply the stronger pulse (keeps your class names) */
        #marketing-host .answer-option.correct.pulse-correct {
          animation: mkStrongPulse 1.05s ease-in-out infinite; /* faster, brighter */
        }

        /* Fallback overlay list (during hero->answers gap) */
        .mk-overlay-list {
          position: absolute; left: 50%; transform: translateX(-50%);
          top: 22vh; width: min(88vw, 1100px);
          display: flex; flex-direction: column; gap: 1.4vh;
          margin: 0; padding: 0; list-style: none; z-index: 6;
        }
        .mk-overlay-list li { display: flex; justify-content: center; }
        .mk-overlay-list .answer-option { width: 100%; }

        /* Ensure the background container is positioned for portals */
        #marketing-host .quiz-bg { position: relative !important; }
      `}</style>

      {/* small page counter in top-left */}
      <div>
        <div
          className="question-prompt"
          style={{
            background: "rgba(10,14,24,0.62)",
            fontWeight: 900,
            color: "#eef4ff",
            padding: "6px 10px",
            borderRadius: 10,
            marginLeft: 8,
          }}
        >
          {idx + 1} / {slides.length}
        </div>
      </div>

      {/* Host/projector view */}
      <div id="marketing-host" ref={hostRef} style={{ position: "absolute", inset: 0 }}>
        <QuizScreen
          q={q}
          timer={1}
          current={currentStep}
          questions={[q]}
          allAnswers={{}}
          vanishingIndexes={visibleAnswerIndexes}
          justVanished={[]}
          gameCode={"MARKETING"}
          joinCode={"DEMO"}
        />

        {/* Side/below info + media (absolutely positioned so it never pushes answers) */}
        {bgEl && sideRect && !heroActive &&
          createPortal(
            <>
              {/* Text plate */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: `${sideRect.left}px`,
                  top: `${sideRect.top}px`,
                  width: `${sideRect.width}px`,
                  height: `${sideRect.textH}px`,
                  background: PLATE_BG,
                  borderRadius: 0,
                  boxShadow:
                    "0 12px 36px rgba(0,0,0,0.30), 0 1px 0 rgba(255,255,255,0.06) inset",
                  zIndex: 4,
                  pointerEvents: "none",
                }}
              />
              {/* Text content */}
              <div
                style={{
                  position: "absolute",
                  left: `${sideRect.left + 14}px`,
                  top: `${sideRect.top + 12}px`,
                  width: `${sideRect.width - 28}px`,
                  height: `${sideRect.textH - 24}px`,
                  zIndex: 6,
                  color: "#eef4ff",
                  fontWeight: 700,
                  lineHeight: 1.25,
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 16,
                  WebkitBoxOrient: "vertical",
                  pointerEvents: "none",
                  whiteSpace: "pre-wrap",
                  textIndent: 0,
                  fontFamily:
                    "var(--main-font, 'Arial Black', Impact, Arial, sans-serif)",
                  textShadow:
                    "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
                  fontSize: "1.35rem",
                }}
              >
                {detailText || DEFAULT_PROMPT}
              </div>

              {/* Media window */}
              <div
                style={{
                  position: "absolute",
                  left: `${sideRect.left}px`,
                  top: `${sideRect.top + sideRect.textH + 12}px`,
                  width: `${sideRect.width}px`,
                  height: `${sideRect.mediaH}px`,
                  background: "rgba(0,0,0,0.18)",
                  borderRadius: 0,
                  overflow: "hidden",
                  zIndex: 4,
                  boxShadow: "0 10px 28px rgba(0,0,0,0.28)",
                }}
              >
                {mediaToShow?.type === "video" ? (
                  <video
                    key={mediaToShow?.src || "video-fallback"}
                    src={mediaToShow?.src}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                ) : (
                  <img
                    key={mediaToShow?.src || "image-fallback"}
                    alt=""
                    src={mediaToShow?.src || brainBg}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                )}
              </div>
            </>,
            bgEl
          )}

        {/* Temporary overlay answers while hero animates */}
        {showOverlay && bgEl &&
          createPortal(
            <ul
              className="mk-overlay-list"
              style={{
                transform: `translateX(calc(-50% - ${listShift}px))`,
              }}
            >
              {s.answers.map((txt, i) => (
                <li key={i}>
                  <div
                    className={`answer-option option-${String.fromCharCode(
                      97 + (i % 7)
                    )}`}
                  >
                    {txt}
                  </div>
                </li>
              ))}
            </ul>,
            bgEl
          )}
      </div>
    </div>
  );
}
